import React, { useState, useEffect } from 'react';
import { useUser } from '@clerk/clerk-react';
import { 
  generateApiKey, 
  listApiKeys, 
  revokeApiKey
} from '../services/unkey';
import { FaKey, FaPlus, FaTrash, FaCopy, FaCheck } from 'react-icons/fa';
import { FiExternalLink, FiMessageSquare } from 'react-icons/fi';

const ApiKeyManagement = () => {
  const { user } = useUser();
  const [apiKeys, setApiKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [revoking, setRevoking] = useState(false);
  const [revokingKeyId, setRevokingKeyId] = useState(null);
  const [showNewKey, setShowNewKey] = useState(false);
  const [newKey, setNewKey] = useState(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [showAIPrompt, setShowAIPrompt] = useState(false);
  const [promptCopied, setPromptCopied] = useState(false);

  // Load API keys on component mount
  useEffect(() => {
    if (user) {
      loadApiKeys();
    }
  }, [user]);

  const loadApiKeys = async () => {
    if (!user) return;
    
    setLoading(true);
    setError(null);
    setSuccessMessage(null);
    
    try {
      const keys = await listApiKeys(user.id);
      setApiKeys(keys || []);
    } catch (err) {
      console.error('Error loading API keys:', err);
      setError('Failed to load API keys');
      setApiKeys([]);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateKey = async () => {
    if (!user) return;
    
    setGenerating(true);
    setError(null);
    
    try {
      const result = await generateApiKey(
        user.id,
        user.fullName || user.username,
        user.primaryEmailAddress?.emailAddress
      );
      
      setNewKey(result);
      setShowNewKey(true);
      await loadApiKeys(); // Refresh the list
    } catch (err) {
      setError('Failed to generate API key');
      console.error('Error generating API key:', err);
    } finally {
      setGenerating(false);
    }
  };

  const handleRevokeKey = async (keyId) => {
    if (!window.confirm('Are you sure you want to revoke this API key? This action cannot be undone.')) {
      return;
    }
    
    setRevoking(true);
    setRevokingKeyId(keyId);
    setError(null);
    
    try {
      await revokeApiKey(keyId, user.id);
      
      // Remove the key from the local state immediately
      setApiKeys(prevKeys => prevKeys.filter(key => key.id !== keyId));
      
      // Show success message
      setError(null);
      setSuccessMessage('API key revoked successfully');
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      setError('Failed to revoke API key: ' + (err.message || 'Unknown error'));
      console.error('Error revoking API key:', err);
    } finally {
      setRevoking(false);
      setRevokingKeyId(null);
    }
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
    }
  };

  const generateAIPrompt = () => {
    // Use the newly generated key if available, otherwise use placeholder
    const apiKey = newKey?.key || 'YOUR_API_KEY';
    const keyInfo = newKey?.key 
      ? `Here's my API key: ${apiKey}`
      : 'I have my ARMS API key ready (I\'ll provide it when needed)';
    
    return `I need help integrating Elsai ARMS (Agent Resource Management System) into my codebase. ${keyInfo}

Please help me:
1. Set up the ARMS SDK in my project
2. Initialize ARMS with my API key
3. Integrate ARMS to monitor everything (ARMS has LLM monitoring, OCR monitoring, agent monitoring, RAG monitoring, etc. - monitor everything that is applicable to my code)

The API key can be set as an environment variable ELSAI_ARMS_API_KEY or passed directly to the ElsaiARMS() constructor.

Documentation: @https://arms.elsaifoundry.ai/documentation

Please provide clear, working code examples for my programming language.`;
  };

  const copyAIPrompt = async () => {
    const prompt = generateAIPrompt();
    try {
      await navigator.clipboard.writeText(prompt);
      setPromptCopied(true);
      setTimeout(() => setPromptCopied(false), 3000);
    } catch (err) {
      console.error('Failed to copy prompt:', err);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };



  if (!user) {
    return (
      <div className="bg-white rounded-lg p-6 shadow-sm">
        <p className="text-gray-500">Please sign in to manage your API keys.</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg p-6 shadow-sm">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">API Key Management</h2>
          <p className="text-sm text-gray-600 mt-1">
            Generate and manage API keys for accessing ARMS services
          </p>
        </div>
        <button
          onClick={handleGenerateKey}
          disabled={generating}
          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <FaPlus className="mr-2" />
          {generating ? 'Generating...' : 'Generate New Key'}
        </button>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
          <p className="text-red-600 text-sm">{error}</p>
        </div>
      )}

      {successMessage && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-md">
          <p className="text-green-600 text-sm">{successMessage}</p>
        </div>
      )}

      {/* Newly Generated Key Display */}
      {showNewKey && newKey && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-md">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-green-800">New API Key Generated</h3>
            <button
              onClick={() => setShowNewKey(false)}
              className="text-green-600 hover:text-green-800"
            >
              ×
            </button>
          </div>
          <div className="bg-white p-3 rounded border">
            <div className="flex items-center justify-between">
              <code className="text-sm font-mono text-gray-800">{newKey.key}</code>
              <button
                onClick={() => copyToClipboard(newKey.key)}
                className="ml-2 p-1 text-gray-500 hover:text-gray-700"
                title="Copy to clipboard"
              >
                {copied ? <FaCheck className="text-green-500" /> : <FaCopy />}
              </button>
            </div>
          </div>
          <p className="text-xs text-green-600 mt-2">
            ⚠️ Make sure to copy this key now. You won't be able to see it again.
          </p>
        </div>
      )}

      {/* API Keys List */}
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">Your API Keys</h3>
        
        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="text-gray-500 mt-2">Loading API keys...</p>
          </div>
        ) : apiKeys.length === 0 ? (
          <div className="text-center py-8">
            <FaKey className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No API keys found</h3>
            <p className="mt-1 text-sm text-gray-500">
              Get started by generating your first API key.
            </p>

            <button
              onClick={handleGenerateKey}
              className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              Generate API Key
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {apiKeys.map((key) => (
              <div
                key={key.id}
                className="border border-gray-200 rounded-lg p-4 hover:border-gray-300 transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3">
                      <FaKey className="text-gray-400" />
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          {key.meta?.name || 'API Key'}
                        </p>
                        <p className="text-xs text-gray-500">
                          Created {formatDate(key.createdAt)}
                        </p>
                      </div>
                    </div>
                    <div className="mt-2">
                      <code className="text-xs font-mono text-gray-600 bg-gray-50 px-2 py-1 rounded">
                        {key.key}
                      </code>
                      <p className="text-xs text-gray-500 mt-1">
                        Full key not stored for security. Copy from generation or use key ID.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => handleRevokeKey(key.id)}
                      disabled={revoking}
                      className="p-2 text-red-400 hover:text-red-600 disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Revoke key"
                    >
                      {revoking && revokingKeyId === key.id ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-600"></div>
                      ) : (
                        <FaTrash />
                      )}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Usage Information */}
      <div className="mt-8 p-4 bg-gray-50 rounded-lg">
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-sm font-medium text-gray-900">How to use your API key</h4>
          <a
            href="https://arms.elsaifoundry.ai/documentation"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center text-sm text-blue-600 hover:text-blue-700 font-medium transition-colors"
          >
            View Documentation
            <FiExternalLink className="ml-1" />
          </a>
        </div>
        <div className="text-sm text-gray-600 space-y-2">
          <p>Add API key to env file:</p>
          <code className="block bg-white p-2 rounded text-xs font-mono">
            ELSAI_ARMS_API_KEY = YOUR_API_KEY
          </code>
          <p>Or as a parameter:</p>
          <code className="block bg-white p-2 rounded text-xs font-mono">
            ElsaiARMS(api_key="YOUR_API_KEY", project_name="YOUR_PROJECT_NAME")
          </code>
        </div>

        {/* AI Assistant Prompt */}
        <div className="mt-4 pt-4 border-t border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center space-x-2">
              <FiMessageSquare className="text-gray-600" />
              <span className="text-sm font-medium text-gray-900">Need help with integration?</span>
            </div>
            <button
              onClick={() => setShowAIPrompt(!showAIPrompt)}
              className="text-sm text-blue-600 hover:text-blue-700 font-medium transition-colors"
            >
              {showAIPrompt ? 'Hide' : 'Show'} AI Prompt
            </button>
          </div>
          
          {showAIPrompt && (
            <div className="mt-3 bg-white rounded-lg border border-gray-200 p-4">
              <p className="text-xs text-gray-600 mb-3">
                Copy this prompt and paste it into Cursor, ChatGPT, Claude, or any AI assistant to get help integrating ARMS into your code:
              </p>
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg p-4 border border-blue-100 relative">
                <pre className="text-xs text-gray-700 font-mono whitespace-pre-wrap overflow-x-auto pr-10">
                  {generateAIPrompt()}
                </pre>
                <button
                  onClick={copyAIPrompt}
                  className="absolute top-3 right-3 p-2 bg-white hover:bg-blue-50 border border-blue-200 rounded-md text-gray-600 hover:text-blue-600 transition-colors shadow-sm"
                  title="Copy prompt"
                >
                  {promptCopied ? (
                    <FaCheck className="text-green-600" />
                  ) : (
                    <FaCopy className="text-gray-500" />
                  )}
                </button>
              </div>
              {promptCopied && (
                <p className="text-xs text-green-600 mt-2 flex items-center">
                  <FaCheck className="mr-1" />
                  Prompt copied to clipboard!
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ApiKeyManagement; 
